import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, MapPin, Pencil, Check, X } from "lucide-react";
import { ConfirmDialog } from "../ConfirmDialog";
import { api } from "../api";
import { UploadZone } from "../../components/common/UploadZone";
import { fileUrl } from "../../context/SiteData";

const SLOTS = [
  { key: "hero", label: "Büyük Hero Görseli", where: "Ana Sayfa — en üstteki tam ekran bölüm" },
  { key: "intro", label: "Tanıtım Görseli", where: "Ana Sayfa — 'Sadece bir yurt değil' bölümü" },
  { key: "photoBreak", label: "Tam Genişlik Fotoğraf", where: "Ana Sayfa ve Hakkımızda — ortadaki büyük fotoğraf bölümü" },
  { key: "about", label: "Hakkımızda Görseli", where: "Hakkımızda sayfası — hikaye bölümü" },
  { key: "room-tek-kisilik", label: "Tek Kişilik Oda", where: "Odalar sayfası kartı + oda detay sayfası" },
  { key: "room-iki-kisilik", label: "İki Kişilik Oda", where: "Odalar sayfası kartı + oda detay sayfası" },
  { key: "hizmet-yemek", label: "Yemek Bölümü Görseli", where: "Hizmetler sayfası — 'Yemekhanemiz' bölümü" },
  { key: "hizmet-sosyal", label: "Teras & Sosyal Alanlar", where: "Hizmetler sayfası — en alttaki geniş bölüm" },
  { key: "videoPoster", label: "Video Kapağı — Tanıtım", where: "Ana Sayfa video bölümü + Videolar sayfası 1. kart" },
  { key: "video2", label: "Video Kapağı — Odalar", where: "Videolar sayfası 2. kart" },
  { key: "video3", label: "Video Kapağı — Manzara", where: "Videolar sayfası 3. kart" },
];

const SlotImage = ({ src, label }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  return (
    <div className="relative mt-3 aspect-[16/9] w-full overflow-hidden bg-cream-200">
      {!loaded && !error && <div className="absolute inset-0 animate-pulse bg-cream-300" />}
      {error ? (
        <div className="flex h-full items-center justify-center text-xs text-ink-400">Görsel yüklenemedi</div>
      ) : (
        <img
          src={src}
          alt={label}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          className={`h-full w-full object-cover transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      )}
    </div>
  );
};

const GorsellerTab = () => {
  const [images, setImages] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [pending, setPending] = useState(null);
  const [pendingAlt, setPendingAlt] = useState("");
  const [editId, setEditId] = useState(null);
  const [editAlt, setEditAlt] = useState("");
  const [confirm, setConfirm] = useState(null); // { message, action }

  const load = () => {
    api.get("/images").then(({ data }) => setImages(data.images)).catch(() => setImages({}));
    api.get("/gallery").then(({ data }) => setGallery(data.items)).catch(() => setGallery([]));
  };
  useEffect(() => { load(); }, []);

  const setSlot = async (slot, path) => {
    await api.put("/admin/images", { slot, path });
    toast.success("Görsel güncellendi — sitede anında yayında");
    load();
  };

  const resetSlot = (slot) => {
    setConfirm({
      message: "Bu bölüm varsayılan görsele dönsün mü?",
      action: async () => {
        await api.delete(`/admin/images/${slot}`);
        toast.success("Bölüm varsayılan görsele döndü");
        load();
      },
    });
  };

  const confirmAddToGallery = async () => {
    if (!pending) return;
    const alt = pendingAlt.trim() || "Yalı Yurt";
    try {
      await api.post("/admin/gallery", { path: pending.path, alt });
      toast.success(`"${alt}" galeriye eklendi`);
      setPending(null);
      setPendingAlt("");
      load();
    } catch {
      toast.error("Eklenemedi, tekrar deneyin");
    }
  };

  const saveAlt = async (id) => {
    if (!editAlt.trim()) {
      toast.error("Başlık boş olamaz");
      return;
    }
    try {
      await api.patch(`/admin/gallery/${id}`, { alt: editAlt.trim() });
      toast.success("Başlık güncellendi");
      setEditId(null);
      load();
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  const removeFromGallery = (id) => {
    setConfirm({
      message: "Bu görsel galeriden kaldırılsın mı?",
      confirmLabel: "Evet, Kaldır",
      action: async () => {
        await api.delete(`/admin/gallery/${id}`);
        toast.success("Görsel kaldırıldı");
        load();
      },
    });
  };

  if (!images || !gallery) return <p className="text-sm text-ink-500">Yükleniyor...</p>;

  return (
    <div data-testid="gorseller-tab">
      <h1 className="text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">Görseller</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-500">
        Aşağıdaki her kart, sitede belli bir bölümü temsil eder. Karttaki fotoğraf, o bölümde
        şu an görünen görseldir. Değiştirmek için altındaki alana yeni görseli sürükleyin;
        kaldırmak isterseniz kartın sağ üstündeki "Sil" butonunu kullanın (bölüm varsayılan görsele döner).
      </p>

      <div className="mt-8 grid gap-5 lg:grid-cols-2">
        {SLOTS.map((s) => (
          <div key={s.key} className="card-lift border border-line bg-white p-5" data-testid={`slot-${s.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-ink-900">{s.label}</p>
                <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-relaxed text-ink-400">
                  <MapPin size={12} className="mt-0.5 shrink-0" />
                  {s.where}
                </p>
              </div>
              {images[s.key] && (
                <button
                  onClick={() => resetSlot(s.key)}
                  data-testid={`slot-reset-${s.key}`}
                  className="icon-btn flex shrink-0 items-center gap-1.5 border border-line px-3 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-ink-500 hover:border-red-600 hover:text-red-600"
                >
                  <Trash2 size={13} />
                  Sil
                </button>
              )}
            </div>
            {images[s.key] ? (
              <SlotImage src={fileUrl(images[s.key])} label={s.label} />
            ) : (
              <p className="mt-3 border border-dashed border-line bg-cream-100 px-4 py-8 text-center text-xs text-ink-400">
                Varsayılan görsel kullanılıyor
              </p>
            )}
            <div className="mt-3">
              <UploadZone testid={`upload-${s.key}`} onUploaded={(path) => setSlot(s.key, path)} label="Değiştirmek için yeni görseli buraya sürükleyin" />
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-12 text-xl font-bold tracking-tight text-ink-900">Galeri Yönetimi</h2>
      <p className="mt-1 text-sm text-ink-500">
        Bu kartlar /galeri sayfasındaki fotoğraflardır. Yeni fotoğraf yüklediğinizde önce başlığını yazarsınız,
        sonra galeriye eklenir. Başlıklar sitede fotoğrafın üzerine gelindiğinde ve tam ekranda görünür.
      </p>

      {pending && (
        <div className="mt-4 max-w-md border border-ink-900 bg-white p-4" data-testid="gallery-pending">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-900">Bu fotoğrafa başlık verin</p>
          <SlotImage src={fileUrl(pending.path)} label="Yeni görsel" />
          <input
            value={pendingAlt}
            onChange={(e) => setPendingAlt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmAddToGallery()}
            placeholder="Örn: Üçüncü kat koridoru, Teras oturma alanı..."
            data-testid="gallery-alt-input"
            className="input-field mt-3"
            autoFocus
          />
          <div className="mt-3 flex gap-2">
            <button onClick={confirmAddToGallery} data-testid="gallery-confirm-add" className="btn btn-primary flex-1 !py-3">
              <Check size={14} /> Galeriye Ekle
            </button>
            <button
              onClick={() => { setPending(null); setPendingAlt(""); }}
              data-testid="gallery-cancel-add"
              className="btn btn-outline-dark !px-4 !py-3"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {!pending && (
        <div className="mt-4 max-w-xs">
          <UploadZone testid="upload-gallery" onUploaded={(path) => { setPending({ path }); setPendingAlt(""); }} label="Galeriye yeni görsel ekleyin" />
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {gallery.length === 0 && (
          <p className="col-span-full border border-dashed border-line bg-white p-8 text-center text-xs text-ink-400">
            Galeride henüz görsel yok
          </p>
        )}
        {gallery.map((g) => (
          <div key={g.id} className="group relative overflow-hidden border border-line bg-white" data-testid={`gallery-admin-${g.id}`}>
            <SlotImage src={fileUrl(g.path)} label={g.alt} />
            {editId === g.id ? (
              <div className="!mt-0 flex items-center gap-1.5 border-t border-line px-2 py-2">
                <input
                  value={editAlt}
                  onChange={(e) => setEditAlt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveAlt(g.id); if (e.key === "Escape") setEditId(null); }}
                  data-testid={`gallery-alt-edit-${g.id}`}
                  className="w-full border border-line bg-cream-50 px-2 py-1.5 text-[11px] outline-none focus:border-ink-900"
                  autoFocus
                />
                <button onClick={() => saveAlt(g.id)} data-testid={`gallery-alt-save-${g.id}`} aria-label="Kaydet" className="icon-btn shrink-0 bg-ink-900 p-1.5 text-cream-50">
                  <Check size={12} />
                </button>
                <button onClick={() => setEditId(null)} aria-label="Vazgeç" className="icon-btn shrink-0 border border-line p-1.5 text-ink-400">
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div className="!mt-0 flex items-center justify-between gap-2 border-t border-line px-3 py-2">
                <span className="truncate text-[11px] font-medium text-ink-500">{g.alt}</span>
                <div className="flex shrink-0 gap-1">
                  <button
                    onClick={() => { setEditId(g.id); setEditAlt(g.alt); }}
                    data-testid={`gallery-edit-${g.id}`}
                    aria-label="Başlığı düzenle"
                    className="icon-btn border border-line p-1.5 text-ink-400 hover:border-ink-900 hover:text-ink-900"
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    onClick={() => removeFromGallery(g.id)}
                    data-testid={`gallery-delete-${g.id}`}
                    className="icon-btn flex items-center gap-1.5 border border-line px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-500 hover:border-red-600 hover:text-red-600"
                  >
                    <Trash2 size={12} />
                    Sil
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!confirm}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const action = confirm?.action;
          setConfirm(null);
          if (action) await action();
        }}
      />
    </div>
  );
};

export default GorsellerTab;
